import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SENHA_MINIMA } from '../utils/erroDeAuth';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import CampoDeSenha from './CampoDeSenha';
import './CompartilharRelatorio.css';
import './LoginPage.css';

/**
 * Criar ou trocar a senha, estando logada (05/10).
 *
 * Até aqui o único caminho era sair e usar "Esqueci minha senha". Serve aos
 * dois casos com o mesmo formulário, e o texto fala dos dois: quem já tem
 * senha e quer outra, e quem sempre entrou com o Google e quer passar a ter
 * uma. Nos dois a senha vale para a MESMA conta.
 *
 * ── Não pede a senha atual ───────────────────────────────────────────────
 *
 * Quem entra com o Google não tem senha atual para informar, e a tela não
 * sabe qual dos dois casos é. A proteção de quem já tem senha é o aviso por
 * email ("A senha da sua conta foi alterada"), ligado no painel do Supabase.
 * Por isso "Require current password when updating" precisa continuar
 * DESLIGADO lá — ver `docs/designs/login-com-senha.md`.
 *
 * ── O modal fica aberto para dizer que deu certo ─────────────────────────
 *
 * Fechar sozinho ao salvar deixaria a dúvida: salvou, ou eu toquei fora? A
 * confirmação fica na tela, com um botão de fechar.
 */
const TrocarSenha: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { definirSenha } = useAuth();
  useTravarScroll();
  useFechavelComVoltar(true, onClose);
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  const [senha, setSenha] = useState('');
  const [repetida, setRepetida] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [salva, setSalva] = useState(false);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (senha.length < SENHA_MINIMA) return setErro(`A senha precisa ter pelo menos ${SENHA_MINIMA} caracteres.`);
    if (senha !== repetida) return setErro('As duas senhas não estão iguais.');
    setOcupado(true);
    setErro(null);
    const falha = await definirSenha(senha);
    setOcupado(false);
    if (falha) return setErro(falha);
    setSenha(''); setRepetida('');
    setSalva(true);
  };

  return (
    <div className="cr-overlay" onClick={onClose} ref={caixa}>
      <div className="cr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Senha de acesso">
        <div className="cr-banda">
          <h2>Senha de acesso</h2>
          <button className="cr-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="cr-corpo">
          {salva ? (
            <div className="login-aviso" role="status">
              <p className="login-aviso-titulo">Senha salva</p>
              <p>
                A partir de agora você também pode entrar com o seu email e essa senha. O que você
                já usava para entrar continua valendo.
              </p>
              <button type="button" className="login-enviar" onClick={onClose}>Fechar</button>
            </div>
          ) : (
            <form className="login-form" onSubmit={salvar} noValidate>
              <p className="login-form-intro">
                Escolha a senha para entrar com o seu email. Se você já tem uma, esta passa a valer
                no lugar. Se sempre entrou com o Google, ele continua funcionando.
              </p>
              <CampoDeSenha
                rotulo="Senha nova" valor={senha} aoMudar={setSenha} autoComplete="new-password"
                desabilitado={ocupado} minLength={SENHA_MINIMA}
                dica={`Pelo menos ${SENHA_MINIMA} caracteres.`}
              />
              <CampoDeSenha
                rotulo="Repita a senha" valor={repetida} aoMudar={setRepetida} autoComplete="new-password"
                desabilitado={ocupado}
              />
              {erro && <p className="login-erro" role="alert">{erro}</p>}
              <button className="login-enviar" disabled={ocupado} aria-busy={ocupado}>Salvar senha</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrocarSenha;
